import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { mensajeDeError } from '../api/error';
import type { EstadoActividad, Monto as TipoMonto } from '../api/tipos';
import { iniciales, monto as formatearMonto } from '../comun/formato';
import { ESTADO_ACTIVIDAD, ESTADO_PAGO, ESTADO_PARTICIPACION, type Tono } from '../comun/textos';
import { Boton } from './Controles';
import { Icono } from './Icono';

export { mensajeDeError };

// --- Estados: SIEMPRE color + ícono + palabra ---------------------------------------

export function Insignia({ tono, children, icono, tamano }: { tono: Tono; children: ReactNode; icono?: string; tamano?: 'normal' | 'grande' }) {
  return (
    <span className={`insignia insignia--${tono} ${tamano === 'grande' ? 'insignia--grande' : ''}`}>
      {icono ? <Icono nombre={icono} /> : null}
      {children}
    </span>
  );
}

export function EstadoParticipacion({ estado }: { estado: 'PENDIENTE' | 'PAGADO' }) {
  const vestido = ESTADO_PARTICIPACION[estado];
  return (
    <Insignia tono={vestido.tono} icono={vestido.icono}>
      {vestido.palabra}
    </Insignia>
  );
}

export function EstadoDeActividad({ estado }: { estado: EstadoActividad }) {
  const vestido = ESTADO_ACTIVIDAD[estado];
  return (
    <Insignia tono={vestido.tono} icono={vestido.icono}>
      {vestido.palabra}
    </Insignia>
  );
}

export function EstadoDePago({ anulado }: { anulado: boolean }) {
  const vestido = ESTADO_PAGO[anulado ? 'ANULADO' : 'VIGENTE'];
  return (
    <Insignia tono={vestido.tono} icono={vestido.icono}>
      {vestido.palabra}
    </Insignia>
  );
}

// --- Montos ------------------------------------------------------------------------

export function Monto({
  valor,
  moneda = 'PYG',
  tono,
  fuerte,
  simbolo = true,
}: {
  valor: TipoMonto | null | undefined;
  moneda?: string;
  tono?: 'pagado' | 'pendiente' | 'debil';
  fuerte?: boolean;
  simbolo?: boolean;
}) {
  return <span className={`monto ${tono ? `monto--${tono}` : ''} ${fuerte ? 'monto--fuerte' : ''}`}>{formatearMonto(valor, moneda, { simbolo })}</span>;
}

/** La cédula se muestra siempre igual: tabular y con puntos, como está impresa en el carnet. */
export function Cedula({ valor, grande }: { valor: string; grande?: boolean }) {
  return <span className={`cedula ${grande ? 'cedula--grande' : ''}`}>{valor.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}</span>;
}

// --- Estructura de página ----------------------------------------------------------

export function Encabezado({
  titulo,
  subtitulo,
  icono,
  acciones,
  children,
}: {
  titulo: string;
  subtitulo?: ReactNode;
  icono?: string;
  acciones?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="encabezado">
      <div className="encabezado__fila">
        <div className="encabezado__titulo">
          {icono ? (
            <span className="encabezado__icono">
              <Icono nombre={icono} />
            </span>
          ) : null}
          <div>
            <h1>{titulo}</h1>
            {subtitulo ? <p>{subtitulo}</p> : null}
          </div>
        </div>
        {acciones ? <div className="encabezado__acciones">{acciones}</div> : null}
      </div>
      {children}
    </header>
  );
}

export function Panel({
  titulo,
  icono,
  acciones,
  children,
  clase,
  sinRelleno,
  estilo,
}: {
  titulo?: ReactNode;
  icono?: string;
  acciones?: ReactNode;
  children: ReactNode;
  clase?: string;
  sinRelleno?: boolean;
  estilo?: CSSProperties;
}) {
  return (
    <section className={`panel ${sinRelleno ? 'panel--sin-relleno' : ''} ${clase ?? ''}`} style={estilo}>
      {titulo ? (
        <header className="panel__cabecera">
          <h2>
            {icono ? <Icono nombre={icono} /> : null}
            {titulo}
          </h2>
          {acciones ? <div className="panel__acciones">{acciones}</div> : null}
        </header>
      ) : null}
      <div className="panel__cuerpo">{children}</div>
    </section>
  );
}

export function Dato({ etiqueta, children, destacado }: { etiqueta: string; children: ReactNode; destacado?: boolean }) {
  return (
    <div className={`dato ${destacado ? 'dato--destacado' : ''}`}>
      <dt>{etiqueta}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export function Avatar({ nombre, tono }: { nombre: string; tono?: 'pagado' | 'pendiente' | 'ausente' }) {
  return (
    <span className={`avatar ${tono ? `avatar--${tono}` : ''}`} aria-hidden>
      {iniciales(nombre)}
    </span>
  );
}

export function Nota({ tono = 'informacion', icono, children }: { tono?: 'informacion' | 'aviso' | 'error' | 'exito'; icono?: string; children: ReactNode }) {
  return (
    <p className={`nota nota--${tono}`} role={tono === 'error' ? 'alert' : undefined}>
      <Icono nombre={icono ?? (tono === 'error' || tono === 'aviso' ? 'alerta' : tono === 'exito' ? 'check' : 'hoja')} />
      <span>{children}</span>
    </p>
  );
}

// --- Estados de carga, vacío y error ------------------------------------------------

export function Cargando({ filas = 4 }: { filas?: number }) {
  return (
    <div className="esqueleto" aria-busy="true" aria-label="Cargando">
      {Array.from({ length: filas }, (_, i) => (
        <span key={i} className="esqueleto__linea" style={{ '--i': i } as CSSProperties} />
      ))}
    </div>
  );
}

export function Vacio({ icono = 'hoja', titulo, texto, accion }: { icono?: string; titulo: string; texto?: string; accion?: ReactNode }) {
  return (
    <div className="vacio">
      <span className="vacio__icono">
        <Icono nombre={icono} />
      </span>
      <strong>{titulo}</strong>
      {texto ? <p>{texto}</p> : null}
      {accion}
    </div>
  );
}

export function ErrorCarga({ error, reintentar }: { error: unknown; reintentar?: () => void }) {
  return (
    <div className="vacio vacio--error" role="alert">
      <span className="vacio__icono">
        <Icono nombre="alerta" />
      </span>
      <strong>{mensajeDeError(error)}</strong>
      {reintentar ? (
        <Boton icono="cambiar" onClick={reintentar}>
          Reintentar
        </Boton>
      ) : null}
    </div>
  );
}

/**
 * Confirmación EN LÍNEA, no un modal: explica la consecuencia antes de hacer algo que no se
 * deshace. "¿Estás seguro?" no dice nada; "lo que quedó pendiente queda pendiente", sí.
 */
export function Confirmacion({
  titulo,
  consecuencia,
  children,
  accion,
  alCancelar,
  cargando,
  textoAccion = 'Sí, continuar',
}: {
  titulo: string;
  consecuencia?: ReactNode;
  children?: ReactNode;
  accion: () => void;
  alCancelar: () => void;
  cargando?: boolean;
  textoAccion?: string;
}) {
  return (
    <div className="confirmacion" role="alertdialog" aria-label={titulo}>
      <strong>{titulo}</strong>
      {consecuencia ? <p>{consecuencia}</p> : null}
      {children}
      <div className="confirmacion__acciones">
        <Boton variante="fantasma" onClick={alCancelar}>
          Cancelar
        </Boton>
        <Boton variante="peligro" cargando={cargando} onClick={accion}>
          {textoAccion}
        </Boton>
      </div>
    </div>
  );
}

// --- Cajón lateral -----------------------------------------------------------------

/**
 * El detalle va AL COSTADO, nunca abajo ni en un modal (criterio de Rodrigo en PLES): sin
 * telón, para poder saltar de una fila a otra sin cerrarlo.
 */
export function Cajon({
  abierto,
  alCerrar,
  titulo,
  subtitulo,
  children,
  pie,
  ancho,
}: {
  abierto: boolean;
  alCerrar: () => void;
  titulo: ReactNode;
  subtitulo?: ReactNode;
  children: ReactNode;
  pie?: ReactNode;
  ancho?: boolean;
}) {
  useEffect(() => {
    if (!abierto) return;
    const alTeclear = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') alCerrar();
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [abierto, alCerrar]);

  if (!abierto) return null;
  return (
    <aside className={`cajon ${ancho ? 'cajon--ancho' : ''}`} role="dialog" aria-modal="false" aria-label={typeof titulo === 'string' ? titulo : undefined}>
      <header className="cajon__cabecera">
        <div>
          <h2>{titulo}</h2>
          {subtitulo ? <p>{subtitulo}</p> : null}
        </div>
        <Boton variante="fantasma" icono="cerrar" onClick={alCerrar} aria-label="Cerrar" />
      </header>
      <div className="cajon__cuerpo">{children}</div>
      {pie ? <footer className="cajon__pie">{pie}</footer> : null}
    </aside>
  );
}

// --- Paginación --------------------------------------------------------------------

/** Filas por página en todas las tablas. Pedido de Rodrigo: las tablas van paginadas SIEMPRE. */
export const POR_PAGINA = 20;

/**
 * Paginación del lado del cliente, para las listas que la API devuelve completas porque son
 * cortas por naturaleza (zonas, tipos, medios, roles). Las que crecen con la operación
 * (integrantes, pagos, auditoría) se paginan en la API.
 */
export function usePaginacion<T>(filas: readonly T[] | undefined, porPagina = POR_PAGINA) {
  const [pagina, setPagina] = useState(1);
  const total = filas?.length ?? 0;
  const ultima = Math.max(1, Math.ceil(total / porPagina));
  const actual = Math.min(pagina, ultima);
  return {
    pagina: actual,
    setPagina,
    porPagina,
    total,
    visibles: (filas ?? []).slice((actual - 1) * porPagina, actual * porPagina),
  };
}

export function Paginador({ pagina, porPagina, total, alCambiar }: { pagina: number; porPagina: number; total: number; alCambiar: (pagina: number) => void }) {
  const paginas = Math.max(1, Math.ceil(total / porPagina));
  if (total === 0) return null;
  return (
    <nav className="paginador" aria-label="Páginas">
      <span>
        {(pagina - 1) * porPagina + 1}–{Math.min(total, pagina * porPagina)} de {total}
      </span>
      <Boton variante="fantasma" tamano="chico" icono="izquierda" disabled={pagina <= 1} onClick={() => alCambiar(pagina - 1)} aria-label="Página anterior" />
      <Boton variante="fantasma" tamano="chico" icono="derecha" disabled={pagina >= paginas} onClick={() => alCambiar(pagina + 1)} aria-label="Página siguiente" />
    </nav>
  );
}

export function SubNav<T extends string>({
  opciones,
  valor,
  alCambiar,
}: {
  opciones: Array<{ valor: T; texto: string; icono?: string; cuenta?: number }>;
  valor: T;
  alCambiar: (valor: T) => void;
}) {
  return (
    <nav className="subnav" aria-label="Secciones">
      {opciones.map((opcion) => (
        <button
          key={opcion.valor}
          type="button"
          className={`subnav__opcion ${valor === opcion.valor ? 'subnav__opcion--activa' : ''}`}
          aria-current={valor === opcion.valor ? 'page' : undefined}
          onClick={() => alCambiar(opcion.valor)}
        >
          {opcion.icono ? <Icono nombre={opcion.icono} /> : null}
          {opcion.texto}
          {opcion.cuenta !== undefined ? <span className="subnav__cuenta">{opcion.cuenta}</span> : null}
        </button>
      ))}
    </nav>
  );
}

/** La tecla que hace falta saber, mostrada como tecla. En el mostrador no se adivina nada. */
export function Tecla({ children }: { children: ReactNode }) {
  return <kbd className="tecla">{children}</kbd>;
}
